import React, { useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FormikProps } from 'formik';
import { Container, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { RootState } from 'slices';

// import {
//   setActiveWorkingTimeId,
//   setWorkingTimeModalOpen,
// } from 'slices/customer/reducer';

import { validationSchema } from './validationSchema';

import { EditWorkingTimeFormType } from 'types/timeAttendance/timeAttendanceTypes';
import {
  setActiveWorkingTimeId,
  setShowAllValidateCustomerErrors,
  setWorkingTimeModalOpen,
} from 'slices/timeAttendance/reducer';
import EditWorkingTimeForm from 'pages/TimeAttendance/WorkingTimeModal/EditWorkingTimeForm';
import { RESPONSE_MESSAGE } from 'pages/TimeAttendance/constants';
import { mapEditWorkingTimeToRequest } from 'utils/mappers/timeAttendanceMapper';
import { putUpdateWorkingTime } from 'services/timeAttendanceService';

const WorkingTimeModal = ({
  fetchTimeAttendance,
}: {
  fetchTimeAttendance: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<EditWorkingTimeFormType>>(null);

  const {
    EditWorkingTimeModalOpen,
    EditWorkingTimeValues,
    countSubmit,
    activeEditWorkingTimeId,
  } = useSelector((state: RootState) => state.TimeAttendance);

  const toggleModal = useCallback(
    (id: string | null = null) => {
      dispatch(setWorkingTimeModalOpen(!EditWorkingTimeModalOpen));
      dispatch(setActiveWorkingTimeId(id));
    },
    [EditWorkingTimeModalOpen, dispatch]
  );

  const closeModal = () => toggleModal(null);

  const submitForm = async (values: EditWorkingTimeFormType) => {
    if (!activeEditWorkingTimeId) return;

    try {
      const response = await putUpdateWorkingTime(
        activeEditWorkingTimeId,
        mapEditWorkingTimeToRequest(values)
      );

      if (response && response.message === 'OK' && response.error_code === 0) {
        toast.success(RESPONSE_MESSAGE.UPDATE_SUCCESS, {
          autoClose: 3000,
        });
        closeModal();
        dispatch(setShowAllValidateCustomerErrors(false));
        fetchTimeAttendance();
      } else {
        toast.error(response.message || 'Error occurred', { autoClose: 3000 });
      }
    } catch (error: any) {
      toast.error(error.message || 'Unexpected error', { autoClose: 3000 });
    }
  };

  useEffect(() => {
    if (formikRef.current?.errors) {
      const errorKeys = Object.keys(formikRef.current.errors);
      const firstErrorField = document.querySelector(
        `[name="${errorKeys[0]}"]`
      ) as HTMLInputElement | null;
      if (firstErrorField) firstErrorField.focus();
    }
  }, [formikRef, countSubmit]);

  return (
    <div className="page-content">
      <Container fluid>
        <Modal
          size="md"
          isOpen={EditWorkingTimeModalOpen}
          toggle={closeModal}
          backdrop="static"
          centered
        >
          <ModalHeader className="bg-light p-3" toggle={closeModal}>
            {t('Edit Working Time')}
          </ModalHeader>
          <ModalBody>
            <Formik
              initialValues={EditWorkingTimeValues}
              validationSchema={validationSchema(t)}
              onSubmit={submitForm}
              enableReinitialize
              innerRef={formikRef}
            >
              {(formik) => (
                <form noValidate onSubmit={formik.handleSubmit}>
                  <EditWorkingTimeForm formik={formik} />
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button type="submit" className="btn btn-success">
                      {t('Save')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </ModalBody>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default WorkingTimeModal;
